import { z } from '@hono/zod-openapi'
import { KATAKANA_ERROR_MESSAGE, KATAKANA_REGEX } from '../lib/validation_lib'

// id
export const idZ = z.number().openapi({ description: '入居者ID', example: 1 })
export type id = z.infer<typeof idZ>

// tenantId: 紐づく賃借人
export const tenantIdZ = z
  .number()
  .openapi({ description: '紐づく賃借人ID', example: 1 })
export type tenantId = z.infer<typeof tenantIdZ>

// name: 氏名
export const nameZ = z
  .string()
  .openapi({ description: '入居者の氏名', example: '鈴木一郎' })
export type name = z.infer<typeof nameZ>

// nameKana: 氏名カナ
// Echoes 側で任意項目のため、未提供を表す空文字を許容する。
export const nameKanaZ = z
  .string()
  .refine((value) => value === '' || KATAKANA_REGEX.test(value), {
    message: KATAKANA_ERROR_MESSAGE,
  })
  .openapi({
    description: '入居者の氏名カナ。未提供時は空文字',
    example: 'スズキイチロウ',
  })
export type nameKana = z.infer<typeof nameKanaZ>

// birthday: 生年月日
export const birthdayZ = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .openapi({ description: '生年月日（YYYY-MM-DD）', example: '1985-08-15' })
export type birthday = z.infer<typeof birthdayZ>

// phone: 電話番号
export const phoneZ = z.string().max(20).nullable().openapi({
  description: '入居者の電話番号。null は未提供',
  example: '09012345678',
})
export type phone = z.infer<typeof phoneZ>

// note: 備考
export const noteZ = z
  .string()
  .max(1000)
  .nullable()
  .openapi({ description: '入居者の備考。null は未提供', example: '同居予定' })
export type note = z.infer<typeof noteZ>

// gender: 性別（Echoes の TenantOccupant.gender と同値）
export const TENANT_OCCUPANT_GENDER_MALE = 'male'
export const TENANT_OCCUPANT_GENDER_FEMALE = 'female'
export const TENANT_OCCUPANT_GENDER_OTHER = 'other'
export const TENANT_OCCUPANT_GENDER_VALUES = [
  TENANT_OCCUPANT_GENDER_MALE,
  TENANT_OCCUPANT_GENDER_FEMALE,
  TENANT_OCCUPANT_GENDER_OTHER,
] as const
export const TENANT_OCCUPANT_GENDERS = [
  { key: TENANT_OCCUPANT_GENDER_MALE, value: '男性' },
  { key: TENANT_OCCUPANT_GENDER_FEMALE, value: '女性' },
  { key: TENANT_OCCUPANT_GENDER_OTHER, value: 'その他' },
] as const

export const genderZ = z
  .enum(TENANT_OCCUPANT_GENDER_VALUES)
  .nullable()
  .openapi({
    description:
      '性別（male:男性 / female:女性 / other:その他）。null は未提供',
    example: TENANT_OCCUPANT_GENDER_MALE,
  })
export type gender = z.infer<typeof genderZ>

// createdAt: 作成日時
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt: 更新日時
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// upsertOccupants: 賃借人の作成・更新リクエストで受け取る入居者一覧。
// 入居者は単体では更新せず、賃借人単位で全置換する（Echoes 同期と同じ扱い）。
export const upsertOccupantsZ = z
  .array(
    z.object({
      name: nameZ,
      nameKana: nameKanaZ,
      phone: phoneZ,
      birthday: birthdayZ,
      note: noteZ,
      gender: genderZ.optional(),
    }),
  )
  .optional()
  .openapi({
    description:
      '入居者の一覧。指定した内容で全置換する（省略時は変更しない、空配列で全削除）',
    example: [
      {
        name: '鈴木一郎',
        nameKana: 'スズキイチロウ',
        phone: '09012345678',
        birthday: '1985-08-15',
        note: '同居予定',
      },
    ],
  })
export type upsertOccupants = z.infer<typeof upsertOccupantsZ>
